/**
 * useProductEditor — Zustand + Speicherablauf des einheitlichen Produkt-Editors.
 *
 * Datensicherheit (siehe lib/productEditorModel.js):
 *   • Produkt: nur Diff-Patch über safeEntityUpdate; stock/reserved/shopify_*
 *     werden nie geschrieben. preis/price und is_active/aktiv immer paarweise.
 *   • Varianten: nie gelöscht (aktiv=false), bestand/shopify_* nie geschrieben.
 *   • Shopify-Push erst nach erfolgreichem DB-Write; Push-Fehler blockieren
 *     den lokalen Save nicht. B2B-Preis und EWK-Felder verlassen FC nie.
 *   • Kundenpreise (Admin): nur BESTEHENDE customer_prices-Zeilen werden
 *     per id aktualisiert (preis + custom_price gemeinsam); nie anlegen,
 *     nie löschen. Fehlgeschlagene Zeilen bleiben als Eingabe erhalten.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { base44 } from '@/api/base44Client';
import { safeEntityUpdate } from '@/lib/safeUpdate';
import { updateCustomerPriceById } from '@/lib/customerPriceApi';
import { diffKundenpreisEdits } from '@/lib/kundenpreisEdits';
import { pushProductTitle, pushVariantDetails } from '@/lib/shopifyPush';
import {
  buildInitialForm, buildCreatePayload, diffProductPatch, diffVariantPatches,
  isDirty, needsShopifyPush, newVariantRow, rowToVariantValues, validateForm,
} from '@/lib/productEditorModel';

const MIGRATION_HINT = 'Migration 20260907_03_ewk_gewicht_grundpreis.sql ausführen.';
const NEW_VARIANT_COLUMNS = [
  'gewicht_kg', 'gewicht_synced_at', 'grundpreis_menge', 'grundpreis_einheit',
  'grundpreis_referenz_menge', 'grundpreis_referenz_einheit', 'grundpreis_anzeigen',
];
const isMissingColumnError = (err) => /column|schema cache/i.test(err?.message || '');

/** create() hat keinen safeEntityUpdate — bei fehlender Spalte einmal ohne neue Felder wiederholen */
async function createVariantSafe(payload) {
  try {
    return await base44.entities.ProductVariant.create(payload);
  } catch (err) {
    if (!isMissingColumnError(err)) throw err;
    const stripped = { ...payload };
    NEW_VARIANT_COLUMNS.forEach((k) => delete stripped[k]);
    const created = await base44.entities.ProductVariant.create(stripped);
    created._skipped = NEW_VARIANT_COLUMNS.filter((k) => k in payload);
    return created;
  }
}

export function useProductEditor({ product, open, onOpenChange }) {
  const queryClient = useQueryClient();
  const productId = product?.id || null;
  const isNew = !productId;

  const variantsQuery = useQuery({
    queryKey: ['product-variants', productId],
    queryFn: () => base44.entities.ProductVariant.filter({ product_id: productId }, 'position'),
    enabled: !!productId && open,
  });
  const { data: images = [] } = useQuery({
    queryKey: ['product-images', productId],
    queryFn: () => base44.entities.ProductImage.filter({ product_id: productId }, 'position'),
    enabled: !!productId && open,
  });
  const { data: kundenpreise = [] } = useQuery({
    queryKey: ['product-kundenpreise', productId],
    queryFn: () => base44.entities.CustomerPrice.filter({ product_id: productId }),
    enabled: !!productId && open,
  });
  const { data: supplierList = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => base44.entities.Supplier.list(),
    enabled: open,
  });
  const ewkQuery = useQuery({
    queryKey: ['ewk-kategorien'],
    queryFn: () => base44.entities.EwkKategorie.list('position'),
    enabled: open,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const [form, setForm] = useState(() => buildInitialForm(null, []));
  const [initial, setInitial] = useState(form);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  // Rohe Eingaben zu bestehenden Kundenpreisen: { [customer_prices.id]: '8,50' }
  const [kundenpreisEdits, setKundenpreisEdits] = useState({});
  const loadedFor = useRef(null);

  const resetFrom = useCallback((p, vs) => {
    const next = buildInitialForm(p, vs || []);
    setForm(next);
    setInitial(next);
    setErrors([]);
    setKundenpreisEdits({});
  }, []);

  // Formular nur beim Öffnen bzw. beim ersten Eintreffen der Varianten aufbauen —
  // NICHT bei jedem Poll der Produktliste (würde laufende Eingaben überschreiben).
  useEffect(() => {
    if (!open) { loadedFor.current = null; return; }
    const key = productId || 'new';
    if (isNew) {
      if (loadedFor.current !== key) { loadedFor.current = key; resetFrom(null, []); }
      return;
    }
    if (variantsQuery.data && loadedFor.current !== key) {
      loadedFor.current = key;
      resetFrom(product, variantsQuery.data);
    }
  }, [open, productId, isNew, product, variantsQuery.data, resetFrom]);

  const kundenpreisDiff = useMemo(
    () => diffKundenpreisEdits(kundenpreise, kundenpreisEdits),
    [kundenpreise, kundenpreisEdits],
  );
  const kundenpreisDirty = kundenpreisDiff.changes.length > 0 || kundenpreisDiff.errors.length > 0;
  const dirty = useMemo(() => isDirty(initial, form) || kundenpreisDirty, [initial, form, kundenpreisDirty]);
  const errorFields = useMemo(() => new Set(errors.map((e) => e.feld)), [errors]);
  const kundenpreisErrorIds = useMemo(
    () => new Set(kundenpreisDiff.errors.map((e) => e.id)),
    [kundenpreisDiff],
  );

  const setKundenpreis = useCallback((id, value) => {
    setKundenpreisEdits((e) => ({ ...e, [id]: value }));
  }, []);

  const setProductField = useCallback((key, value) => {
    setForm((f) => ({ ...f, product: { ...f.product, [key]: value } }));
  }, []);
  const setRow = useCallback((rowKey, patch) => {
    setForm((f) => ({ ...f, rows: f.rows.map((r) => (r._key === rowKey ? { ...r, ...patch } : r)) }));
  }, []);
  const addRow = useCallback(() => {
    setForm((f) => {
      const active = f.rows.filter((r) => !r._removed);
      const position = active.length + 1;
      return { ...f, rows: [...f.rows, newVariantRow(position, { is_default: active.length === 0 })] };
    });
  }, []);
  const removeRow = useCallback((rowKey) => {
    setForm((f) => ({
      ...f,
      rows: f.rows
        .map((r) => (r._key === rowKey ? { ...r, _removed: true } : r))
        .filter((r) => !(r._removed && !r.id)), // nie gespeicherte Zeilen sofort verwerfen
    }));
  }, []);

  const invalidateAll = useCallback((id) => {
    queryClient.invalidateQueries(['products']);
    queryClient.invalidateQueries(['product-variants-all']);
    if (id) {
      queryClient.invalidateQueries(['product-variants', id]);
      queryClient.invalidateQueries(['product-kundenpreise', id]);
      queryClient.invalidateQueries(['product-customer-prices', id]);
    }
  }, [queryClient]);

  /**
   * Geänderte Kundenpreise einzeln schreiben. Läuft NACH dem Produkt-Save;
   * ein Fehler stoppt die übrigen Zeilen nicht. Erfolgreiche Eingaben werden
   * verworfen (frische DB-Werte folgen), fehlgeschlagene bleiben stehen.
   */
  const saveKundenpreise = useCallback(async () => {
    const { changes } = kundenpreisDiff;
    const ok = [];
    const failed = [];
    for (const c of changes) {
      try {
        await updateCustomerPriceById(c.id, c.neu);
        ok.push(c);
      } catch (err) {
        failed.push({ ...c, message: err?.message || String(err) });
      }
    }
    if (ok.length > 0) {
      setKundenpreisEdits((e) => {
        const next = { ...e };
        ok.forEach((c) => delete next[c.id]);
        return next;
      });
      new Set(ok.map((c) => c.lead_id).filter(Boolean)).forEach((leadId) => {
        queryClient.invalidateQueries(['customer-prices', leadId]);
      });
    }
    return { ok, failed };
  }, [kundenpreisDiff, queryClient]);

  const saveExisting = useCallback(async () => {
    const skipped = new Set();
    const patch = diffProductPatch(product, form.product);
    if (Object.keys(patch).length > 0) {
      const res = await safeEntityUpdate(base44.entities.Product, productId, patch);
      res.skipped.forEach((k) => skipped.add(k));
    }

    const { updates, creates, deactivates } = diffVariantPatches(form.rows);
    const now = new Date().toISOString();
    const pushed = [];
    for (const u of updates) {
      const res = await safeEntityUpdate(base44.entities.ProductVariant, u.id, { ...u.patch, updated_date: now });
      res.skipped.forEach((k) => skipped.add(k));
      if (u.row.shopify_variant_id && needsShopifyPush(u.patch)) {
        pushed.push({ ...u.row._orig, ...rowToVariantValues(u.row), _patch: u.patch });
      }
    }
    for (const c of creates) {
      const created = await createVariantSafe({ ...c.payload, product_id: productId, updated_date: now });
      (created?._skipped || []).forEach((k) => skipped.add(k));
    }
    for (const d of deactivates) {
      await base44.entities.ProductVariant.update(d.id, { aktiv: false, updated_date: now });
    }

    // Shopify-Spiegel (nur verknüpfte Produkte; B2B/EWK nie)
    const msgs = [];
    if (product.shopify_product_id) {
      if (patch.name && patch.name !== product.name) {
        const t = await pushProductTitle(product, patch.name);
        msgs.push(t.ok ? 'Titel → Shopify ✓' : 'Titel: ' + t.message);
      }
      if (pushed.length > 0) {
        const d = await pushVariantDetails(product, pushed);
        msgs.push(d.ok ? `${d.count} Variante(n) → Shopify ✓` : 'Varianten: ' + d.message);
        if (d.ok) {
          for (const v of pushed.filter((x) => 'gewicht_kg' in x._patch)) {
            await base44.entities.ProductVariant.update(v.id, { gewicht_synced_at: now }).catch(() => {});
          }
        }
      }
    }
    return { skipped: [...skipped], msgs };
  }, [product, productId, form]);

  const saveNew = useCallback(async () => {
    const created = await base44.entities.Product.create(buildCreatePayload(form.product));
    const skipped = new Set();
    try {
      const { creates } = diffVariantPatches(form.rows);
      const now = new Date().toISOString();
      for (let i = 0; i < creates.length; i++) {
        const payload = { ...creates[i].payload, product_id: created.id, updated_date: now };
        if (i === 0) {
          payload.is_default = true;
          if (!payload.sku && created.sku) payload.sku = created.sku;
        }
        const v = await createVariantSafe(payload);
        (v?._skipped || []).forEach((k) => skipped.add(k));
      }
    } catch (inner) {
      // Produkt ohne Varianten wäre unbrauchbar → kompensierend entfernen
      await base44.entities.Product.delete(created.id).catch(() => {});
      throw inner;
    }
    return { id: created.id, skipped: [...skipped] };
  }, [form]);

  const save = useCallback(async () => {
    const errs = validateForm(form.product, form.rows);
    setErrors(errs);
    if (errs.length > 0) { toast.error(errs[0].meldung); return false; }
    if (kundenpreisDiff.errors.length > 0) { toast.error(kundenpreisDiff.errors[0].meldung); return false; }
    setSaving(true);
    try {
      if (isNew) {
        const { id, skipped } = await saveNew();
        toast.success('Produkt erstellt');
        if (skipped.length) toast.warning(`Feld(er) übersprungen (Spalte fehlt in DB): ${skipped.join(', ')} — ${MIGRATION_HINT}`);
        invalidateAll(id);
        onOpenChange(false);
        return true;
      }
      const { skipped, msgs } = await saveExisting();
      const kp = await saveKundenpreise();
      if (kp.failed.length > 0) {
        toast.error(`Kundenpreis nicht gespeichert (${kp.failed.map((f) => f.lead_name || f.id).join(', ')}): ${kp.failed[0].message}`);
      }
      toast.success(kp.ok.length > 0 ? `Produkt gespeichert · ${kp.ok.length} Kundenpreis(e) aktualisiert` : 'Produkt gespeichert');
      if (skipped.length) toast.warning(`Feld(er) übersprungen (Spalte fehlt in DB): ${skipped.join(', ')} — ${MIGRATION_HINT}`);
      if (msgs.length) toast.info(msgs.join(' · '));
      invalidateAll(productId);
      // Formular aus frischen DB-Daten neu aufbauen (kein Stale-Form)
      const [fresh, freshVariants] = await Promise.all([
        base44.entities.Product.get(productId),
        base44.entities.ProductVariant.filter({ product_id: productId }, 'position'),
      ]);
      queryClient.setQueryData(['product-variants', productId], freshVariants);
      const failedEdits = Object.fromEntries(kp.failed.map((f) => [f.id, kundenpreisEdits[f.id]]));
      resetFrom(fresh || product, freshVariants);
      if (kp.failed.length > 0) setKundenpreisEdits(failedEdits);
      return kp.failed.length === 0;
    } catch (err) {
      toast.error('Speichern fehlgeschlagen: ' + (err?.message || err));
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, isNew, saveNew, saveExisting, saveKundenpreise, kundenpreisDiff, kundenpreisEdits, invalidateAll, onOpenChange, productId, product, queryClient, resetFrom]);

  const requestClose = useCallback(() => {
    if (saving) return;
    if (dirty && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
    onOpenChange(false);
  }, [dirty, saving, onOpenChange]);

  return {
    isNew, form, dirty, saving, errors, errorFields,
    variants: variantsQuery.data || [],
    variantsLoading: !isNew && variantsQuery.isLoading,
    images, kundenpreise, supplierList,
    kundenpreisEdits, setKundenpreis, kundenpreisErrorIds, kundenpreisDirty,
    ewkKategorien: ewkQuery.data || [],
    ewkError: ewkQuery.error,
    setProductField, setRow, addRow, removeRow, setForm,
    save, requestClose, invalidateAll,
  };
}
